<!-- eslint-disable vue/no-v-html -->
<template>
    <div
        class="ask-pendo-message"
        :class="{
            'ask-pendo-message__user': isUser,
            'ask-pendo-message__assistant': isAssistant,
            'ask-pendo-message__error': isError,
            'ask-pendo-message__guide-chat': isGuideChat,
        }">
        <div
            v-if="!isGuideChat && isAssistant"
            class="ask-pendo-message__assistant-icon">
            <img
                height="16"
                width="16"
                src="@img/pendo-chevron.svg"
                :alt="$t('askPendo.assistantIcon.Pendo Icon')">
        </div>
        <div
            v-if="showTimestamp && formattedTimestamp"
            class="ask-pendo-message__timestamp">
            {{ formattedTimestamp }}
        </div>
        <div class="ask-pendo-message__reasoning">
            <slot name="reasoning" />
        </div>
        <div
            v-if="uploadedFileName"
            class="ask-pendo-message__file">
            <ask-pendo-file-context
                :file-name="uploadedFileName"
                :file-icon="uploadedFileIcon" />
        </div>
        <div class="ask-pendo-message__content">
            <pendo-icon
                v-if="isGuideChat"
                size="16"
                :class="`ask-pendo-message__icon`"
                :type="iconProps.type"
                :fill="iconProps.fill"
                :stroke-width="iconProps.strokeWidth"
                :stroke="iconProps.stroke" />
            <div
                ref="renderedMarkdown"
                class="ask-pendo-message__markdown"
                v-html="renderedContent" />
        </div>
        <div class="ask-pendo-message__artifact">
            <slot name="artifact" />
            <slot name="artifact-preview" />
        </div>
        <div class="ask-pendo-message__feedback">
            <slot name="feedback" />
        </div>
        <ask-pendo-mention-popover
            v-if="showMentionPopover"
            :mention-data="mentionData"
            :reference-element="popoverReferenceElement"
            :segment-dependents="segmentDependents"
            @mouseenter="onPopoverMouseEnter"
            @mouseleave="onPopoverMouseLeave" />
    </div>
</template>

<script>
import moment, { DATE_FORMAT } from '@/utils/moment-wrapper';
import { renderMarkdown } from '@/common/marked/markdown.utils.js';
import { mapState } from 'vuex';
import { PendoIcon } from '@pendo/components';
import { isAssistantMessage, isErrorMessage, isUserMessage } from '@/ask-pendo/utils/message';
import { parseMessageContent } from '@/vuex/modules/ask-pendo/ask-pendo.utils';
import AskPendoFileContext from '@/ask-pendo/components/ask-pendo-file-context.vue';
import AskPendoMentionPopover from '@/ask-pendo/components/ask-pendo-mention-popover.vue';
import mentionPopoverMixin from '@/ask-pendo/utils/mention-popover.mixin.js';
import { getFileIcon } from '@/ask-pendo/utils/file-upload.js';

export default {
    name: 'AskPendoMessage',
    components: {
        PendoIcon,
        AskPendoFileContext,
        AskPendoMentionPopover,
    },
    mixins: [mentionPopoverMixin],
    props: {
        message: {
            type: Object,
            required: true,
        },
        showTimestamp: {
            type: Boolean,
            default: false,
        },
        isGuideChat: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['send-message'],
    computed: {
        ...mapState({
            segmentDependents: (state) => state.segments.segmentDependents,
        }),
        content () {
            return parseMessageContent(this.message).content || '';
        },
        formattedTimestamp () {
            if (!this.isUser || !this.timestamp) {
                return '';
            }

            return moment(this.timestamp, { ignoreTimeZone: true }).format(
                `${DATE_FORMAT.fullDate} ${DATE_FORMAT.time}`,
            );
        },
        isAssistant () {
            return isAssistantMessage(this.message);
        },
        isError () {
            return isErrorMessage(this.message);
        },
        isUser () {
            return isUserMessage(this.message);
        },
        iconProps () {
            return {
                type: this.isUser ? 'user' : 'logo',
                stroke: this.isUser ? 'var(--palette-gray-70)' : 'var(--palette-pink-110)',
                strokeWidth: this.isUser ? 2 : 1,
                fill: this.isUser ? 'transparent' : 'var(--palette-pink-110)',
            };
        },
        renderedContent () {
            if (!this.content) return '';

            return renderMarkdown(this.content);
        },
        timestamp () {
            return this.message.timestamp;
        },
        uploadedFile () {
            // Only one file supported; use first if present
            return this.message?.metadata?.files?.[0] || null;
        },
        uploadedFileName () {
            return this.uploadedFile?.name || '';
        },
        uploadedFileIcon () {
            return getFileIcon(this.uploadedFile?.type);
        },
    },
    mounted () {
        const markdownContainer = this.$refs.renderedMarkdown;
        markdownContainer?.addEventListener('click', this.onClickableElementClick);
    },
    beforeUnmount () {
        const markdownContainer = this.$refs.renderedMarkdown;
        markdownContainer?.removeEventListener('click', this.onClickableElementClick);
    },
    methods: {
        // Used by mentionPopoverMixin
        // eslint-disable-next-line internal-rules/no-unused-properties
        getMentionPopoverContainer () {
            return this.$refs.renderedMarkdown;
        },
        onClickableElementClick (event) {
            if (!this.isAssistant) return;

            const clickableListItem = event.target.closest('li[data-clickable-list-item="true"]');
            if (clickableListItem) {
                // Calculate the list position (1-based index)
                const parentList = clickableListItem.parentElement;
                if (parentList) {
                    const listItems = Array.from(parentList.children);
                    const position = listItems.indexOf(clickableListItem) + 1;

                    // Send just the number
                    this.$emit('send-message', { text: String(position) });

                    return;
                }
            }

            // Check for clickable option (not in a list)
            const clickableOption = event.target.closest('.clickable-option');
            if (clickableOption && !clickableOption.closest('li[data-clickable-list-item="true"]')) {
                const optionText = clickableOption.getAttribute('data-text');
                if (optionText) {
                    this.$emit('send-message', { text: optionText });
                }

                return;
            }

            // Check for mention (not in a list)
            const mention = event.target.closest('.mention');
            if (mention && !mention.closest('li[data-clickable-list-item="true"]')) {
                const optionText = mention.getAttribute('data-text');
                if (optionText) {
                    this.$emit('send-message', { text: optionText });
                }
            }
        },
    },
};
</script>

<style scoped lang="less">
@import "@/common/marked/markdown-styles";
.ask-pendo-message {
    position: relative;
    width: 100%;
    scroll-margin-top: 20px;

    &__assistant-icon {
        position: absolute;
        left: 0;

        display: flex;
        align-items: center;
        justify-content: center;
        height: 28px;
    }

    &__timestamp {
        font-size: 12px;
        color: var(--palette-gray-70);
        margin-bottom: 4px;
        text-align: right;
    }

    &__user {
        align-items: flex-end;

        .ask-pendo-message__file {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 4px;
        }

        .ask-pendo-message__content {
            background-color: var(--palette-gray-20);
            margin-left: auto;
            padding: 12px 16px;
            max-width: 80%;
            width: fit-content;
        }
    }

    &__icon {
        &--assistant {
            color: var(--palette-pink-110);
        }

        :deep(.ask-pendo-message__content) {
            span.mention {
                background-color: var(--palette-gray-20);
                &:hover {
                    background-color: var(--palette-gray-40);
                }
            }
        }
    }

    &__assistant {
        align-items: flex-start;
        padding-left: 28px;

        .ask-pendo-message__content {
            text-align: left;
            margin-right: auto;
        }
    }

    &__error {
        align-items: flex-start;

        .ask-pendo-message__content {
            background-color: var(--palette-red-10);
            color: var(--palette-red-90);
            border: 1px solid var(--palette-red-30);
            text-align: left;
            margin-right: auto;
            padding: 12px;
        }
    }

    &__content {
        display: flex;
        gap: 8px;
        border-radius: 8px;
        word-wrap: break-word;
        word-break: break-word;
        overflow-wrap: break-word;

        .pendo-icon {
            margin-top: 6px;
        }
    }

    &__markdown {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__artifact {
        width: 100%;
    }

    &__content p {
        margin: 0 0 0 10px !important;
    }
}

// guide chat specific styles
.ask-pendo-message__guide-chat {
    .ask-pendo-message {
        &__content {
            padding: 16px 32px 16px 8px;
            width: 100%;
            max-width: 100%;
            align-items: flex-start;
        }
    }
}

// Markdown styles
:deep(.ask-pendo-message__markdown) {
    .markdown-styles()
}
</style>
